import { zodResolver } from '@hookform/resolvers/zod';
import { InlineAlertType } from '@varo/graphql-schema-types';
import { isValid as isValidDate, parse } from 'date-fns';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { useStepStartedFocusAnalytics } from 'features/application/hooks/useStepStartedFocusAnalytics';
import {
  UpdateApplicationData,
  UpdateApplicationHandler,
} from 'features/application/hooks/useUpdateApplication';
import { DOBVerificationSchema } from 'features/application/screens/DateOfBirth/DateOfBirth.schema';
import {
  DATE_18_YEARS_AGO,
  DATE_120_YEARS_AGO,
} from 'features/signup/utils/signupUtils';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Keyboard,
  KeyboardAvoidingView,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import DatePicker from 'shared/components/vxl/DatePicker/DatePicker';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isIOS } from 'shared/constants/platform';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useKeyboardVisibility from 'shared/hooks/keyboard/useKeyboardVisibility';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { getKeyboardVerticalOffset } from 'shared/utils/keyboardUtils';
import {
  DATE_INPUT_MAXLENGTH,
  DATE_MM_DD_YYYY,
  formatDate,
  formatDateInputText,
} from 'shared/utils/string/date';
import { UseMutationState } from 'urql';

const FORM_SET_VALUE_OPTIONS = {
  shouldValidate: true,
  shouldDirty: true,
  shouldTouch: true,
} as const;

type DateOfBirthRoute =
  | '/reactivation/review-info'
  | '/signup/address-collection'
  | '/signup/employment';

interface DateOfBirthProps {
  prefilledDateOfBirth: string;
  updateApplication: UpdateApplicationHandler;
  updateApplicationResult: UseMutationState<UpdateApplicationData>;
  isUpdateApplicationLoading: boolean;
  isUpdateApplicationSuccess: boolean;
  reExecuteQuery: () => void;
  fetching: boolean;
  error?: Error | null;
  nextRoute: DateOfBirthRoute;
  backRoute: DateOfBirthRoute;
  buttonLabel: string;
  isReactivation?: boolean;
  hasApplicationLoaded: boolean;
  isReapplication: boolean;
}

const DateOfBirth = ({
  nextRoute,
  backRoute,
  prefilledDateOfBirth,
  fetching,
  error,
  reExecuteQuery,
  updateApplication,
  updateApplicationResult,
  isUpdateApplicationSuccess,
  isUpdateApplicationLoading,
  buttonLabel,
  isReactivation,
  hasApplicationLoaded,
  isReapplication,
}: DateOfBirthProps) => {
  const router = useRouter();
  const { height } = useWindowDimensions();
  const isKeyboardVisible = useKeyboardVisibility();
  const { bottom } = useSafeAreaInsets();
  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'dateOfBirth',
  });

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      dateOfBirth: '',
    },
    resolver: zodResolver(DOBVerificationSchema),
  });

  const enteredDateOfBirth =
    useWatch({
      control,
      name: 'dateOfBirth',
    }) ?? '';

  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const inputAnchorRef = useRef<View>(null);

  const parsedEnteredDateOfBirth = parse(
    enteredDateOfBirth,
    'MM/dd/yyyy',
    new Date(),
  );
  const isEnteredDateOfBirthComplete =
    DATE_MM_DD_YYYY.test(enteredDateOfBirth) &&
    isValidDate(parsedEnteredDateOfBirth);

  const datePickerValue = isEnteredDateOfBirthComplete
    ? parsedEnteredDateOfBirth
    : undefined;

  const handleBackButtonAction = () => {
    router.replace(backRoute);
  };

  const handleOpenDatePicker = () => {
    Keyboard.dismiss();
    setIsDatePickerOpen((prev) => !prev);
  };

  useBackHandler(handleBackButtonAction);

  useEffect(() => {
    if (prefilledDateOfBirth) {
      reset({
        dateOfBirth: formatDate(prefilledDateOfBirth, 'MM/dd/yyyy'),
      });
    }
  }, [prefilledDateOfBirth, reset]);

  const stepStartedAnalyticsConfig = hasApplicationLoaded
    ? {
        analyticsId: 'stepStarted',
        customAttributes: {
          step_name: 'personal_info_dob',
          ...(isReactivation && {
            reactivation_flow: true,
          }),
          ...(isReapplication && {
            reapplication_flow: 'true',
          }),
        },
      }
    : undefined;

  useStepStartedFocusAnalytics(stepStartedAnalyticsConfig);

  const dobCompletedAnalyticsConfig = isUpdateApplicationSuccess
    ? {
        analyticsId: 'dobCompleted',
        customAttributes: {
          step_name: 'personal_info_dob',
          ...(isReactivation && {
            reactivation_flow: true,
          }),
          ...(isReapplication && {
            reapplication_flow: 'true',
          }),
        },
      }
    : undefined;

  useStepStartedFocusAnalytics(dobCompletedAnalyticsConfig);

  const onSubmit = async () => {
    const { dateOfBirth } = getValues();
    const parsedDate = parse(dateOfBirth, 'MM/dd/yyyy', new Date());
    await updateApplication({
      updates: {
        dateOfBirth: formatDate(parsedDate, 'yyyy-MM-dd'),
      },
      nextRoute,
    });
  };

  const keyboardBehavior = isIOS ? 'padding' : 'height';
  const containerPaddingBottom = isKeyboardVisible ? 0 : bottom;

  const errorComponent = error ? (
    <DefaultPageDownErrorMessage
      error={error}
      title={defaultTitle}
      message={defaultMessage}
      illustrationKey="PLACEHOLDER_A"
      tryAgainAction={reExecuteQuery}
      screenAnalyticsEventKey="signupFlowError"
    />
  ) : null;

  return (
    <>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={handleBackButtonAction}
            />
          ),
        }}
      />

      <View
        style={[styles.container, { paddingBottom: containerPaddingBottom }]}
      >
        {updateApplicationResult.error ? (
          <View style={styles.inlineAlertContainer}>
            <InlineAlert
              alertType={InlineAlertType.Error}
              bodyText={translatedText('inlineAlert.errorTitle')}
              iconKeyType="ERROR"
              onDismiss={() => {}}
            />
          </View>
        ) : null}

        <KeyboardAvoidingView
          style={styles.keyboardContainer}
          behavior={keyboardBehavior}
          keyboardVerticalOffset={getKeyboardVerticalOffset(
            height,
            isKeyboardVisible,
          )}
        >
          <ScrollView contentContainerStyle={styles.scrollContent}>
            <View style={styles.dobFormContainer}>
              <TitleLgText numberOfLines={1}>
                {translatedText('title')}
              </TitleLgText>

              <Controller
                control={control}
                name="dateOfBirth"
                render={({ field: { onChange, onBlur, value } }) => {
                  const handleChange = (text: string) => {
                    const formattedText = formatDateInputText(text);
                    onChange(formattedText);
                    if (formattedText.length === DATE_INPUT_MAXLENGTH) {
                      onBlur();
                    }
                  };
                  return (
                    <View ref={inputAnchorRef} style={styles.input}>
                      <VXLTextInput
                        testID="text-input-subTitle"
                        labelText={translatedText('subTitle')}
                        errorText={errors.dateOfBirth?.message ?? ''}
                        onChangeText={handleChange}
                        onBlur={onBlur}
                        placeholder="MM/DD/YYYY"
                        inputMode="numeric"
                        keyboardType="numeric"
                        analyticsId="dobClicked"
                        value={value}
                        maxLength={DATE_INPUT_MAXLENGTH}
                        onSubmitEditing={handleSubmit(onSubmit)}
                        rightIcon={{
                          iconKey: 'CALENDAR',
                          iconAccessibilityLabel: 'date-icon',
                          onPress: handleOpenDatePicker,
                        }}
                      />
                    </View>
                  );
                }}
              />

              <DatePicker
                anchorRef={inputAnchorRef}
                value={datePickerValue}
                maximumDate={DATE_18_YEARS_AGO}
                minimumDate={DATE_120_YEARS_AGO}
                isOpen={isDatePickerOpen}
                display="bottomsheet"
                onDismiss={() => {
                  setIsDatePickerOpen(false);
                }}
                onClear={() => {
                  setValue('dateOfBirth', '', FORM_SET_VALUE_OPTIONS);
                  setIsDatePickerOpen(false);
                }}
                onValueChange={(date) => {
                  setValue(
                    'dateOfBirth',
                    formatDate(date, 'MM/dd/yyyy'),
                    FORM_SET_VALUE_OPTIONS,
                  );
                }}
              />
            </View>
          </ScrollView>

          <View style={styles.buttonContainer}>
            <Button
              label={buttonLabel}
              onPress={handleSubmit(onSubmit)}
              isLoading={fetching || isUpdateApplicationLoading}
              isDisabled={!isValid}
              size="LARGE"
              type="PRIMARY"
              width="FULL"
            />
          </View>
        </KeyboardAvoidingView>
      </View>

      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={
          <View
            style={styles.loaderContainer}
            testID="inline-loader"
            accessible
          >
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={errorComponent}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'flex-start',
    padding: theme.spacing.m,
  },
  dobFormContainer: {
    flex: 1,
  },
  inlineAlertContainer: {
    paddingVertical: theme.spacing.m,
  },
  scrollContent: {
    flexGrow: 1,
  },
  input: {
    marginTop: theme.spacing.m,
    width: '100%',
  },
  buttonContainer: {
    paddingBottom: theme.spacing.m,
  },
  keyboardContainer: {
    flex: 1,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
  },
});

export default DateOfBirth;
